import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2 } from "lucide-react";
import ReorderList from "../components/ReorderList";

const TAXONOMIES = [
  { key: "case_projects", label: "Projetos", hint: "Aparece como checkboxes no editor de Case. Ex: Branding, Lançamento, Rebrand…" },
  { key: "verticals", label: "Verticais", hint: "Tipo do empreendimento. Ex: Vertical, Horizontal, Loteamento, Multipropriedade…" },
  { key: "padroes", label: "Padrão", hint: "Ex: Popular, Alto Padrão, Luxo, Triple A…" },
  { key: "countries", label: "Países", hint: "Aparece no dropdown de País do case." },
  { key: "team_roles", label: "Funções da equipe", hint: "Autocomplete no campo Equipe Principal. Ex: Designer, Diretor de Arte, Mídia Digital…" },
  { key: "insight_topics", label: "Assuntos dos Insights", hint: "Assuntos/tópicos disponíveis no editor e no filtro público de Insights. Ex: Branding, IA, Mercado…" },
  { key: "case_categories", label: "Categorias de Case", hint: "Rótulos gerais dos cases (exibidos como badges). Ex: Lançamento, Rebranding, Campanha…" },
];

export default function TaxonomiesEditor() {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState({});

  useEffect(() => {
    Promise.all(TAXONOMIES.map(t => api.get(`/content/kv/${t.key}`).catch(() => ({})))).then((results) => {
      const next = {};
      TAXONOMIES.forEach((t, i) => { next[t.key] = Array.isArray(results[i]?.data) ? results[i].data : []; });
      setData(next);
    });
  }, []);

  const upd = (key, i, v) => setData(d => { const a = [...(d[key] || [])]; a[i] = v; return { ...d, [key]: a }; });
  const add = (key) => setData(d => ({ ...d, [key]: [...(d[key] || []), ""] }));
  const remove = (key, i) => setData(d => ({ ...d, [key]: (d[key] || []).filter((_, j) => j !== i) }));

  const save = async (key) => {
    setSaving(s => ({ ...s, [key]: true }));
    try {
      const cleaned = (data[key] || []).map(v => (v || "").trim()).filter(Boolean);
      await api.put(`/content/kv/${key}`, { value: cleaned });
      setData(d => ({ ...d, [key]: cleaned }));
      toast.success("Salvo.");
    } catch { toast.error("Erro ao salvar."); }
    finally { setSaving(s => ({ ...s, [key]: false })); }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-display text-4xl">Taxonomias</h1>
        <p className="text-sm text-neutral-500 mt-1 max-w-2xl">
          Listas reutilizáveis pelos editores. Adicione, edite ou remova opções para <b>Projetos</b>, <b>Verticais</b>, <b>Padrão</b>, <b>Países</b> e <b>Funções da equipe</b>. As alterações refletem imediatamente no editor de cases e nos filtros públicos.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {TAXONOMIES.map((t) => (
          <section key={t.key} className="border border-black/10 rounded-md p-5" data-testid={`tax-${t.key}`}>
            <div className="flex items-end justify-between gap-3 mb-3">
              <div>
                <div className="font-semibold text-sm">{t.label}</div>
                <div className="text-[11px] text-neutral-500">{t.hint}</div>
              </div>
              <button onClick={() => save(t.key)} disabled={saving[t.key]} className="text-xs bg-black text-white px-3 py-1.5 rounded-full font-semibold hover:bg-accent-orange transition-colors disabled:opacity-50" data-testid={`tax-save-${t.key}`}>
                <Save className="w-3 h-3 inline mr-1" /> {saving[t.key] ? "…" : "Salvar"}
              </button>
            </div>
            <ReorderList
              items={data[t.key] || []}
              onChange={(arr) => setData(d => ({ ...d, [t.key]: arr }))}
              getKey={(_v, i) => `${t.key}-${i}`}
              renderItem={(val, i) => (
                <input value={val || ""} onChange={(e) => upd(t.key, i, e.target.value)} className="w-full border border-black/15 px-3 py-1.5 rounded text-sm" data-testid={`tax-${t.key}-${i}`} />
              )}
            />
            <button onClick={() => add(t.key)} className="mt-3 text-xs bg-accent-yellow px-3 py-1.5 rounded-full font-semibold hover:bg-black hover:text-white transition-colors" data-testid={`tax-add-${t.key}`}>
              <Plus className="w-3 h-3 inline" /> Adicionar
            </button>
          </section>
        ))}
      </div>
    </div>
  );
}
